// src/pages/MapsPage.jsx
//
// OpenStreetMap directions page:
//  - Leaflet map with OpenStreetMap tiles (no API key required)
//  - Origin ("current location") and destination inputs, geocoded via
//    Nominatim, with a "Use my location" button for the browser's GPS
//  - Turn-by-turn route + distance/duration from the OSRM public routing API
//  - "Start Journey" button that live-tracks the user's position on the map,
//    showing remaining distance and an ETA until the destination
//
// URL params: /maps?from=Origin+Address&to=Destination+Address
import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import LocationInput from '../components/LocationInput';
import { reverseGeocode } from '../utils/geo';

const OSM_TILE_URL = 'https://tile.openstreetmap.org/{z}/{x}/{y}.png';
const OSM_ATTRIBUTION = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors';
const NOMINATIM_URL = 'https://nominatim.openstreetmap.org/search';
const OSRM_URL = 'https://router.project-osrm.org/route/v1/driving';

// --- Helpers -----------------------------------------------------------------

const makePinIcon = (color) =>
  L.divIcon({
    className: 'paymeo-map-pin',
    html: `<div style="width:26px;height:26px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);background:${color};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);"></div>`,
    iconSize: [26, 26],
    iconAnchor: [13, 26],
  });

const makeLiveIcon = () =>
  L.divIcon({
    className: 'paymeo-map-live',
    html: `<div style="width:22px;height:22px;border-radius:50%;background:#2563eb;border:3px solid #fff;box-shadow:0 0 0 8px rgba(37,99,235,.25),0 2px 8px rgba(0,0,0,.3);"></div>`,
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  });

async function geocode(query) {
  const params = new URLSearchParams({ format: 'json', limit: '1', countrycodes: 'ng', q: query });
  const res = await fetch(`${NOMINATIM_URL}?${params.toString()}`, {
    headers: { Accept: 'application/json' },
  });
  if (!res.ok) throw new Error('Geocoding failed');
  const data = await res.json();
  if (!data.length) throw new Error(`Could not find "${query}"`);
  const hit = data[0];
  return {
    lat: parseFloat(hit.lat),
    lon: parseFloat(hit.lon),
    name: hit.display_name,
  };
}

async function fetchRoute(origin, destination) {
  const coords = `${origin.lon},${origin.lat};${destination.lon},${destination.lat}`;
  const params = new URLSearchParams({
    overview: 'full',
    geometries: 'geojson',
    steps: 'true',
  });
  const res = await fetch(`${OSRM_URL}/${coords}?${params.toString()}`);
  if (!res.ok) throw new Error('Routing service failed');
  const data = await res.json();
  if (data.code !== 'Ok' || !data.routes || !data.routes.length) {
    throw new Error('No route found between these locations');
  }
  const route = data.routes[0];
  const steps = (route.legs || []).flatMap((leg) =>
    (leg.steps || []).map((step) => ({
      instruction: step.maneuver?.instruction || step.name || 'Continue',
      name: step.name || '',
      distance: step.distance || 0,
      duration: step.duration || 0,
      type: step.maneuver?.type || 'depart',
      modifier: step.maneuver?.modifier || '',
      location: step.maneuver?.location || null,
    }))
  );
  return {
    coordinates: route.geometry?.coordinates || [],
    distance: route.distance || 0, // meters
    duration: route.duration || 0, // seconds
    steps,
  };
}

// Haversine distance in meters between two {lat, lon} points
function haversine(a, b) {
  const R = 6371000;
  const toRad = (deg) => (deg * Math.PI) / 180;
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);
  const s =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return 2 * R * Math.asin(Math.sqrt(s));
}

function formatDistance(meters) {
  if (meters == null || Number.isNaN(meters)) return '—';
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1)} km`;
}

function formatDuration(seconds) {
  if (seconds == null || Number.isNaN(seconds)) return '—';
  const mins = Math.round(seconds / 60);
  if (mins < 60) return `${mins} min`;
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  return rem > 0 ? `${hrs} hr ${rem} min` : `${hrs} hr`;
}

function maneuverIcon(type, modifier) {
  switch (type) {
    case 'depart':
      return '🚩';
    case 'arrive':
      return '🏁';
    case 'turn':
    case 'new name':
      return modifier === 'left' ? '⬅️' : modifier === 'right' ? '➡️' : '↪️';
    case 'continue':
      return '⬆️';
    case 'merge':
    case 'on ramp':
      return '↗️';
    case 'roundabout':
    case 'rotary':
      return '🔃';
    case 'exit':
      return '↘️';
    case 'end of road':
      return '🛑';
    default:
      return '➡️';
  }
}

// --- Component ---------------------------------------------------------------

const MapsPage = () => {
  const [searchParams] = useSearchParams();

  const [originQuery, setOriginQuery] = useState('');
  const [destinationQuery, setDestinationQuery] = useState('');
  const [origin, setOrigin] = useState(null); // { lat, lon, name }
  const [destination, setDestination] = useState(null); // { lat, lon, name }
  const [route, setRoute] = useState(null); // { coordinates, distance, duration, steps }
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [locating, setLocating] = useState(false);
  const [originAccuracy, setOriginAccuracy] = useState(null);

  // Which field the next map click should set ('origin' | 'destination' | null)
  const [mapPickMode, setMapPickMode] = useState(null);
  const mapPickModeRef = useRef(null);
  useEffect(() => {
    mapPickModeRef.current = mapPickMode;
  }, [mapPickMode]);

  // Journey tracking state
  const [journeyStarted, setJourneyStarted] = useState(false);
  const [livePosition, setLivePosition] = useState(null); // { lat, lon, accuracy }
  const [remainingDistance, setRemainingDistance] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);

  // Leaflet refs
  const mapRef = useRef(null);
  const mapContainerRef = useRef(null);
  const routeLayerRef = useRef(null); // FeatureGroup holding markers + polyline
  const liveMarkerRef = useRef(null);
  const watchIdRef = useRef(null);
  const journeyIntervalRef = useRef(null);
  const destinationRef = useRef(null);
  const lastSetViewRef = useRef(null);
  const retriedRef = useRef(false);

  destinationRef.current = destination;

  // --- Map lifecycle ----------------------------------------------------------
  useEffect(() => {
    if (mapRef.current || !mapContainerRef.current) return;

    const map = L.map(mapContainerRef.current, {
      center: [9.0579, 7.4951], // Nigeria center as a sensible default
      zoom: 6,
      zoomControl: true,
    });
    L.tileLayer(OSM_TILE_URL, { attribution: OSM_ATTRIBUTION, maxZoom: 19 }).addTo(map);
    routeLayerRef.current = L.featureGroup().addTo(map);
    mapRef.current = map;

    // Click-to-set: when the user has picked a field (origin/destination), a
    // click anywhere on the map drops the pin there and reverse-geocodes it.
    map.on('click', async (e) => {
      const mode = mapPickModeRef.current;
      if (!mode) return;
      const { lat, lng } = e.latlng;
      let name = `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      try {
        name = await reverseGeocode(lat, lng);
      } catch (err) {
        // Keep the coordinates as the fallback name
      }
      if (mode === 'origin') {
        setOrigin({ lat, lon: lng, name });
        setOriginQuery(name);
        setOriginAccuracy(null);
      } else {
        setDestination({ lat, lon: lng, name });
        setDestinationQuery(name);
      }
      setMapPickMode(null);
    });

    // Ensure the map lays out correctly after first paint
    requestAnimationFrame(() => map.invalidateSize());

    return () => {
      if (watchIdRef.current != null) {
        navigator.geolocation.clearWatch(watchIdRef.current);
        watchIdRef.current = null;
      }
      if (journeyIntervalRef.current) {
        clearInterval(journeyIntervalRef.current);
        journeyIntervalRef.current = null;
      }
      map.remove();
      mapRef.current = null;
      routeLayerRef.current = null;
      liveMarkerRef.current = null;
    };
  }, []);

  // --- Prefill from URL params (?from=...&to=...) ------------------------------
  useEffect(() => {
    const from = searchParams.get('from');
    const to = searchParams.get('to');
    if (from) setOriginQuery(from);
    if (to) setDestinationQuery(to);
    const run = async () => {
      try {
        if (from && to) {
          const [o, d] = await Promise.all([geocode(from), geocode(to)]);
          setOrigin(o);
          setDestination(d);
        } else if (from) {
          setOrigin(await geocode(from));
        } else if (to) {
          setDestination(await geocode(to));
        }
      } catch (e) {
        setError(e.message);
      }
    };
    if (from || to) run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // --- Draw route on the map ----------------------------------------------------
  useEffect(() => {
    const map = mapRef.current;
    const layer = routeLayerRef.current;
    if (!map || !layer) return;

    layer.clearLayers();

    if (origin) {
      L.marker([origin.lat, origin.lon], { icon: makePinIcon('#16a34a'), zIndexOffset: 500 })
        .bindPopup(`<b>Origin</b><br/>${origin.name}`)
        .addTo(layer);
    }
    if (destination) {
      L.marker([destination.lat, destination.lon], { icon: makePinIcon('#dc2626'), zIndexOffset: 500 })
        .bindPopup(`<b>Destination</b><br/>${destination.name}`)
        .addTo(layer);
    }

    if (route?.coordinates?.length) {
      L.polyline(route.coordinates.map(([lon, lat]) => [lat, lon]), {
        color: '#2563eb',
        weight: 6,
        opacity: 0.85,
      }).addTo(layer);
    }

    const points = [];
    if (origin) points.push([origin.lat, origin.lon]);
    if (destination) points.push([destination.lat, destination.lon]);
    if (points.length === 2) {
      map.fitBounds(L.latLngBounds(points).pad(0.2));
      lastSetViewRef.current = Date.now();
    } else if (points.length === 1) {
      // A single point: zoom in on it instead of fitting the whole country.
      map.setView(points[0], 15);
      lastSetViewRef.current = Date.now();
    }
  }, [origin, destination, route]);

  // --- Actions ------------------------------------------------------------------

  const useMyLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      return;
    }
    setLocating(true);
    setError(null);
    retriedRef.current = false;

    const onPosition = async (pos) => {
      const { latitude: lat, longitude: lon, accuracy } = pos.coords;
      setOriginAccuracy(accuracy);
      // Reverse-geocode so the user sees a readable place name and can confirm
      // the fix landed where they actually are.
      let name = 'My current location';
      try {
        name = await reverseGeocode(lat, lon);
      } catch (e) {
        // Keep the fallback name
      }
      setOrigin({ lat, lon, name });
      setOriginQuery(name);
      setLocating(false);
    };

    const onError = (err) => {
      // Some browsers/hardware fail with enableHighAccuracy — retry once with
      // the default accuracy before giving up.
      if (!retriedRef.current) {
        retriedRef.current = true;
        navigator.geolocation.getCurrentPosition(onPosition, onError, {
          enableHighAccuracy: false,
          timeout: 10000,
          maximumAge: 0,
        });
        return;
      }
      setLocating(false);
      setError(
        err.code === err.PERMISSION_DENIED
          ? 'Location permission denied. Type your location instead.'
          : 'Could not get your location. Type your location instead.'
      );
    };

    navigator.geolocation.getCurrentPosition(onPosition, onError, {
      enableHighAccuracy: true,
      timeout: 15000,
      maximumAge: 0,
    });
  }, []);

  const getDirections = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      let o = origin;
      const oQuery = originQuery.trim();
      // Re-geocode when the text was edited, but keep a geolocation fix that
      // was set with the "My current location" button.
      if (oQuery && oQuery !== 'My current location' && (!o || o.name !== oQuery)) {
        o = await geocode(oQuery);
        setOrigin(o);
      }

      let d = destination;
      const dQuery = destinationQuery.trim();
      if (dQuery && (!d || d.name !== dQuery)) {
        d = await geocode(dQuery);
        setDestination(d);
      }

      if (!o || !d) {
        setError('Please provide both a current location and a destination.');
        return;
      }

      const r = await fetchRoute(o, d);
      setRoute(r);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [origin, destination, originQuery, destinationQuery]);

  const startJourney = useCallback(() => {
    if (!route || !destination) return;
    if (!navigator.geolocation) {
      setError('Geolocation is not supported by this browser');
      return;
    }
    setJourneyStarted(true);
    setError(null);
    setElapsedTime(0);

    // Track elapsed time
    journeyIntervalRef.current = setInterval(() => {
      setElapsedTime((t) => t + 1);
    }, 1000);

    // If we already have an origin fix, seed the remaining distance
    if (origin) {
      setRemainingDistance(haversine(origin, destination));
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const position = { lat: pos.coords.latitude, lon: pos.coords.longitude, accuracy: pos.coords.accuracy };
        setLivePosition(position);
        const dest = destinationRef.current;
        if (dest) setRemainingDistance(haversine(position, dest));

        const map = mapRef.current;
        if (map) {
          if (!liveMarkerRef.current) {
            liveMarkerRef.current = L.marker([position.lat, position.lon], {
              icon: makeLiveIcon(),
              zIndexOffset: 1000,
            }).addTo(map);
          } else {
            liveMarkerRef.current.setLatLng([position.lat, position.lon]);
          }
          // Pan smoothly, but not on every tick if we just set the view
          const now = Date.now();
          if (!lastSetViewRef.current || now - lastSetViewRef.current > 5000) {
            map.panTo([position.lat, position.lon], { animate: true });
          }
        }
      },
      (err) => {
        setError('Could not track your position. Continuing with the plotted route.');
      },
      { enableHighAccuracy: true, maximumAge: 1000, timeout: 15000 }
    );
  }, [route, destination, origin]);

  const endJourney = useCallback(() => {
    setJourneyStarted(false);
    setLivePosition(null);
    setRemainingDistance(null);
    setElapsedTime(0);
    if (watchIdRef.current != null) {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
    if (journeyIntervalRef.current) {
      clearInterval(journeyIntervalRef.current);
      journeyIntervalRef.current = null;
    }
    if (liveMarkerRef.current && mapRef.current) {
      mapRef.current.removeLayer(liveMarkerRef.current);
      liveMarkerRef.current = null;
    }
  }, []);

  const swapLocations = useCallback(() => {
    setOrigin(destination);
    setDestination(origin);
    setOriginQuery(destination?.name || '');
    setDestinationQuery(origin?.name || '');
    setRoute(null);
  }, [origin, destination]);

  // --- Render --------------------------------------------------------------------

  const hasRoute = !!route;
  const arrived = remainingDistance !== null && remainingDistance < 30;

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-[1400px] mx-auto px-3 py-4 sm:px-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <span className="text-2xl">🗺️</span> Maps &amp; Directions
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Route from your current location to your destination — powered by OpenStreetMap.
            </p>
          </div>
          {route && !journeyStarted && (
            <a
              href={`https://www.openstreetmap.org/directions?from=${origin.lat},${origin.lon}&to=${destination.lat},${destination.lon}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-800 text-white text-sm font-semibold rounded-xl hover:bg-gray-900 transition-colors w-full sm:w-auto justify-center"
            >
              Open in OSM <span aria-hidden>↗</span>
            </a>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3.5 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="grid lg:grid-cols-[380px_1fr] gap-4">
          {/* Left panel: controls + directions */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-4 sm:p-5 h-fit">
            {/* Origin */}
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Current location</label>
            <div className="flex gap-2">
              <LocationInput
                value={originQuery}
                onChange={setOriginQuery}
                onSelect={setOrigin}
                onEnter={getDirections}
                placeholder="Address, city or landmark"
                className="flex-1 min-w-0"
              />
              <button
                onClick={useMyLocation}
                disabled={locating}
                className="shrink-0 inline-flex items-center gap-1.5 px-3.5 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 disabled:opacity-60 transition-colors"
                title="Use my current location"
              >
                {locating ? (
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                ) : (
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                )}
                <span className="hidden sm:inline">My location</span>
              </button>
            </div>
            {origin && (
              <p className="text-xs text-gray-500 mt-1.5 truncate">
                <span className="text-green-600 font-medium">●</span> {origin.name}
                {originAccuracy ? ` (within ~${Math.round(originAccuracy)} m)` : ''}
              </p>
            )}
            <button
              type="button"
              onClick={() => setMapPickMode(mapPickMode === 'origin' ? null : 'origin')}
              className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-2.5 py-1.5 border transition-colors ${
                mapPickMode === 'origin'
                  ? 'bg-green-50 border-green-300 text-green-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <span aria-hidden>📍</span> Pick on map
            </button>

            {/* Swap */}
            <div className="flex justify-center my-2">
              <button
                onClick={swapLocations}
                title="Swap locations"
                className="p-2 rounded-full bg-gray-100 hover:bg-gray-200 text-gray-600 transition-colors"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" />
                </svg>
              </button>
            </div>

            {/* Destination */}
            <label className="block text-sm font-semibold text-gray-700 mb-1.5">Destination</label>
            <LocationInput
              value={destinationQuery}
              onChange={setDestinationQuery}
              onSelect={setDestination}
              onEnter={getDirections}
              placeholder="Address, city or landmark"
            />
            {destination && (
              <p className="text-xs text-gray-500 mt-1.5 truncate">
                <span className="text-red-600 font-medium">●</span> {destination.name}
              </p>
            )}
            <button
              type="button"
              onClick={() => setMapPickMode(mapPickMode === 'destination' ? null : 'destination')}
              className={`mt-1.5 inline-flex items-center gap-1.5 text-xs font-semibold rounded-lg px-2.5 py-1.5 border transition-colors ${
                mapPickMode === 'destination'
                  ? 'bg-red-50 border-red-300 text-red-700'
                  : 'border-gray-200 text-gray-500 hover:border-gray-300 hover:text-gray-700'
              }`}
            >
              <span aria-hidden>📍</span> Pick on map
            </button>

            <button
              onClick={getDirections}
              disabled={loading}
              className="mt-4 w-full bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold py-3 rounded-xl shadow-lg hover:shadow-xl disabled:opacity-60 disabled:cursor-not-allowed transition-all duration-200 inline-flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  Getting directions...
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
                  </svg>
                  Get Directions
                </>
              )}
            </button>

            {/* Route summary */}
            {hasRoute && (
              <div className="mt-4 p-4 rounded-xl bg-blue-50 border border-blue-100">
                <div className="flex items-center justify-between gap-2">
                  <div className="text-center flex-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Distance</p>
                    <p className="text-lg font-bold text-gray-900">{formatDistance(route.distance)}</p>
                  </div>
                  <div className="w-px h-10 bg-blue-200" />
                  <div className="text-center flex-1">
                    <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Est. time</p>
                    <p className="text-lg font-bold text-gray-900">{formatDuration(route.duration)}</p>
                  </div>
                </div>

                {/* Journey controls */}
                {!journeyStarted ? (
                  <button
                    onClick={startJourney}
                    className="mt-4 w-full bg-green-600 hover:bg-green-700 text-white font-bold py-3.5 rounded-xl shadow-lg hover:shadow-xl transition-all duration-200 inline-flex items-center justify-center gap-2"
                  >
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M8 5v14l11-7z" />
                    </svg>
                    Start Journey
                  </button>
                ) : (
                  <div className="mt-4 space-y-3">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-gray-700">
                        {arrived ? '🎉 You have arrived!' : '🚗 On the way'}
                      </span>
                      <span className="text-gray-500">⏱ {formatDuration(elapsedTime)}</span>
                    </div>
                    {remainingDistance !== null && (
                      <div>
                        <div className="flex items-center justify-between text-sm mb-1">
                          <span className="text-gray-600 font-medium">Remaining</span>
                          <span className="font-bold text-gray-900">{formatDistance(remainingDistance)}</span>
                        </div>
                        {!arrived && (
                          <p className="text-xs text-gray-500">
                            ETA ~ {formatDuration((remainingDistance / 1000 / 40) * 3600)} at 40 km/h
                          </p>
                        )}
                      </div>
                    )}
                    <button
                      onClick={endJourney}
                      className="w-full bg-red-600 hover:bg-red-700 text-white font-semibold py-3 rounded-xl transition-colors"
                    >
                      End Journey
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Turn-by-turn directions */}
            {hasRoute && route.steps.length > 0 && (
              <div className="mt-5">
                <h3 className="text-sm font-bold text-gray-900 uppercase tracking-wide mb-2">
                  Directions
                </h3>
                <ol className="max-h-72 overflow-y-auto space-y-2 pr-1">
                  {route.steps.map((step, idx) => (
                    <li key={idx} className="flex items-start gap-3 p-2.5 rounded-lg bg-gray-50 border border-gray-100">
                      <span className="text-lg shrink-0 mt-0.5">{maneuverIcon(step.type, step.modifier)}</span>
                      <div className="min-w-0">
                        <p className="text-sm text-gray-800 leading-snug">{step.instruction}</p>
                        {step.name && <p className="text-xs text-gray-500 truncate">{step.name}</p>}
                      </div>
                      <span className="ml-auto shrink-0 text-xs font-medium text-gray-500 mt-0.5">
                        {formatDistance(step.distance)}
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>

          {/* Right panel: map */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div
              ref={mapContainerRef}
              className="w-full h-[420px] sm:h-[560px] lg:h-[calc(100vh-160px)] lg:min-h-[560px] z-0"
              aria-label="Map showing the route between current location and destination"
            />
            {mapPickMode && (
              <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-blue-200 bg-blue-50 text-blue-700 text-sm">
                <span className="animate-pulse" aria-hidden>📍</span>
                Click anywhere on the map to set your{' '}
                <b>{mapPickMode === 'origin' ? 'current location' : 'destination'}</b>
                <button
                  type="button"
                  onClick={() => setMapPickMode(null)}
                  className="ml-1 text-xs font-semibold underline hover:text-blue-900"
                >
                  Cancel
                </button>
              </div>
            )}
            {!origin && !destination && (
              <div className="flex items-center justify-center gap-2 px-4 py-3 border-t border-gray-100 bg-blue-50 text-blue-700 text-sm">
                <span>📍</span>
                Enter a current location and destination (or add <code className="bg-blue-100 px-1.5 py-0.5 rounded">?from=...&amp;to=...</code> to the URL) to plot a route.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapsPage;
